"use client";

import React, { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { fetchWithAuth } from "@/lib/MemoBE/fetchWithAuth";

type QaAccessResponse = {
  hasAccess: boolean;
  reasonCode?: string | null;
};

type QaIssueCreateResponse = {
  id: string;
  status?: string;
  pageId?: string;
  routeKey?: string;
};

interface ReporterProps {
  locale: string;
  mode?: "floating" | "inline";
}

const issueTypeOptions = [
  "ui",
  "functionality",
  "content",
  "performance",
  "accessibility",
  "data",
] as const;

const severityOptions = ["low", "medium", "high", "blocking"] as const;

const normalizeReportedPath = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  let pathOnly = trimmed;

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const parsed = new URL(trimmed);
      pathOnly = parsed.pathname || "/";
    } catch {
      return null;
    }
  }

  const queryIndex = pathOnly.indexOf("?");
  const hashIndex = pathOnly.indexOf("#");
  const cutoffIndex = [queryIndex, hashIndex]
    .filter((value) => value >= 0)
    .sort((a, b) => a - b)[0];

  if (typeof cutoffIndex === "number") {
    pathOnly = pathOnly.slice(0, cutoffIndex);
  }

  pathOnly = pathOnly.trim();

  if (!pathOnly) {
    return null;
  }

  if (!pathOnly.startsWith("/")) {
    pathOnly = `/${pathOnly}`;
  }

  return pathOnly;
};

const Reporter: React.FC<ReporterProps> = ({ locale, mode = "floating" }) => {
  const pathname = usePathname();
  const [access, setAccess] = useState<QaAccessResponse | null>(null);
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [reportedUrlInput, setReportedUrlInput] = useState("");
  const [description, setDescription] = useState("");
  const [issueType, setIssueType] = useState<(typeof issueTypeOptions)[number]>("functionality");
  const [severity, setSeverity] = useState<(typeof severityOptions)[number]>("medium");
  const [isBug, setIsBug] = useState(true);

  const pagePath = useMemo(() => pathname || `/${locale}`, [locale, pathname]);

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      setIsLoadingAccess(true);
      try {
        const response = await fetchWithAuth("/api/qa/access/me", "GET");
        const parsed = (response as Record<string, unknown>) || {};

        if (!cancelled) {
          setAccess({
            hasAccess: parsed.hasAccess === true,
            reasonCode: typeof parsed.reasonCode === "string" ? parsed.reasonCode : null,
          });
        }
      } catch {
        if (!cancelled) {
          setAccess({ hasAccess: false, reasonCode: "access_check_failed" });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAccess(false);
        }
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async () => {
    setError(null);
    setSuccess(null);

    const normalizedPagePath = normalizeReportedPath(reportedUrlInput);
    if (!normalizedPagePath) {
      setError("URL is required. Use a full URL or a path like /en/tutors.");
      return;
    }

    if (!description.trim()) {
      setError("Description is required.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        title: `Issue on ${normalizedPagePath}`,
        description: description.trim(),
        issueType,
        severity,
        isBug,
        locale,
        pagePath: normalizedPagePath,
        pageUrl: normalizedPagePath,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        viewportWidth: typeof window !== "undefined" ? window.innerWidth : undefined,
        viewportHeight: typeof window !== "undefined" ? window.innerHeight : undefined,
        workspace: "memo-frontend",
      };

      const created = await fetchWithAuth("/api/qa/issues", "POST", payload);
      const parsed = created as QaIssueCreateResponse;
      setSuccess(`Issue registered: ${parsed.id}`);
      setReportedUrlInput("");
      setDescription("");
      setIssueType("functionality");
      setSeverity("medium");
      setIsBug(true);
    } catch (submitError: unknown) {
      const message = submitError instanceof Error ? submitError.message : String(submitError);
      setError(message || "Failed to register issue.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === "inline" && isLoadingAccess) {
    return (
      <div className="card" style={{ borderRadius: 12 }}>
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 text-muted">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            Checking QA reporting access...
          </div>
        </div>
      </div>
    );
  }

  if (mode === "inline" && !access?.hasAccess) {
    return (
      <div className="card" style={{ borderRadius: 12 }}>
        <div className="card-body">
          <div className="alert alert-warning mb-0">
            You do not currently have access to report QA issues.
            {access?.reasonCode ? ` Reason: ${access.reasonCode}.` : ""}
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingAccess || !access?.hasAccess) {
    return null;
  }

  const issueForm = (
    <div className="d-flex flex-column gap-3">
      <div>
        <label className="form-label">URL</label>
        <input
          className="form-control"
          value={reportedUrlInput}
          onChange={(event) => setReportedUrlInput(event.target.value)}
          placeholder="/en/tutors or https://example.com/en/tutors"
          maxLength={400}
        />
        <div className="form-text">Only the path is stored, for example /en/tutors.</div>
      </div>

      <div>
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          rows={5}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="What happened, what did you expect, and steps to reproduce"
        />
      </div>

      <div className="row g-2">
        <div className="col-md-6">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={issueType}
            onChange={(event) => setIssueType(event.target.value as (typeof issueTypeOptions)[number])}
          >
            {issueTypeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Severity</label>
          <select
            className="form-select"
            value={severity}
            onChange={(event) => setSeverity(event.target.value as (typeof severityOptions)[number])}
          >
            {severityOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-check">
        <input
          id="qa-is-bug"
          className="form-check-input"
          type="checkbox"
          checked={isBug}
          onChange={(event) => setIsBug(event.target.checked)}
        />
        <label className="form-check-label" htmlFor="qa-is-bug">
          Mark as bug
        </label>
      </div>

      {error && <div className="alert alert-danger mb-0">{error}</div>}
      {success && <div className="alert alert-success mb-0">{success}</div>}
    </div>
  );

  if (mode === "inline") {
    return (
      <div className="card" style={{ borderRadius: 12 }}>
        <div className="card-body d-flex flex-column gap-3">
          <div>
            <h5 className="mb-1">Report Issue</h5>
            <p className="mb-0 text-muted">Submit a QA issue for the current page context.</p>
          </div>
          {issueForm}
          <div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onSubmit()}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Issue"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-danger"
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 1040,
          borderRadius: 999,
          padding: "10px 16px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        }}
        onClick={() => setIsOpen(true)}
      >
        Report Issue
      </button>

      {isOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
            onClick={() => setIsOpen(false)}
            role="presentation"
          />
          <div className="modal d-block" role="dialog" aria-modal="true" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content" style={{ borderRadius: 12 }}>
                <div className="modal-header">
                  <h5 className="modal-title">Report Issue</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setIsOpen(false)} />
                </div>
                <div className="modal-body d-flex flex-column gap-3">
                  {issueForm}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setIsOpen(false)}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void onSubmit()}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Issue"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Reporter;
